import StandardBlockModel from 'Models/Blocks/StandardBlock/StandardBlockModel.interface';
import XHtmlArea from 'Molecules/XHtmlArea/XHtmlArea';
import { applyEditModeAttr } from 'Shared/Common/Helpers';
import useMedia from 'Shared/Hooks/useMedia';
import { styled } from 'stitches.config';
import { mediaQueryTypes } from 'Theme/Settings/mediaQueries';
import { baseHeadingStyle } from 'Atoms/Typography/Headings/BaseHeadingStyle';
import GridItem from 'Atoms/Grids/GridItem';

type PropTypes = {
  content: StandardBlockModel;
};

function StandardBlock({
  content: {
    heading,
    image,
    secondaryImage,
    rightPositioned,
    bodyText,
    inEditMode,
    subheading,
  },
}: PropTypes) {
  const isMobile = useMedia(mediaQueryTypes.mediaMaxLarge);

  const imageStyling =
    secondaryImage && rightPositioned
      ? `url(${image.src}?preset=standard-block-right-main)`
      : rightPositioned
      ? `url(${image.src}?preset=standard-block-right-main)`
      : image && `url(${image.src}?preset=standard-block-left-main)`;

  const isSplitImage = secondaryImage !== undefined;

  return (
    <GridItem layout="FullWidth" css={GridItemCss}>
      <Root>
        <Column
          css={{
            flexDirection: isMobile
              ? 'column'
              : rightPositioned
              ? 'row-reverse'
              : 'row',
          }}
        >
          <ImageContainer
            css={{
              flexDirection:
                secondaryImage && !rightPositioned ? 'row' : 'row-reverse',
            }}
          >
            {image && (
              <ImageArea
                isSplitImage={isSplitImage}
                {...applyEditModeAttr(inEditMode && 'Image')}
                css={{
                  backgroundImage: imageStyling,
                }}
              ></ImageArea>
            )}
            {secondaryImage && (
              <SecondaryImageArea
                {...applyEditModeAttr(inEditMode && 'Image')}
                css={{
                  backgroundImage: `url(${secondaryImage.src}?preset=standard-block-secondary)`,
                  marginLeft:
                    secondaryImage && !rightPositioned && isMobile
                      ? '8px'
                      : secondaryImage && !rightPositioned
                      ? '32px'
                      : '',
                  marginRight: secondaryImage && !rightPositioned ? '0px' : '',
                }}
              ></SecondaryImageArea>
            )}
          </ImageContainer>

          <TextContainer
            css={{
              justifyContent: isMobile
                ? 'center'
                : rightPositioned
                ? 'flex-end'
                : 'flex-start',
            }}
            rightPositioned={rightPositioned}
          >
            <TextWrapper>
              <SubHeadingWrapper>{subheading} </SubHeadingWrapper>

              <Heading {...applyEditModeAttr(inEditMode && 'Heading')}>
                {heading.toUpperCase()}
              </Heading>
              <BodyWrapper>
                <XHtmlArea
                  css={{ lineHeight: '$lh15' }}
                  {...applyEditModeAttr(inEditMode && 'BodyText')}
                  content={bodyText}
                />
              </BodyWrapper>
            </TextWrapper>
          </TextContainer>
        </Column>
      </Root>
    </GridItem>
  );
}

export default StandardBlock;

const Root = styled('div', {
  gridColumnStart: 'auto',
  gridColumnEnd: 'span 12',
  flexWrap: 'wrap',
  display: 'flex',
  justifyContent: 'center',
  w: '100%',
  '&:nth-child(n)[data-blocklayout="1/1"]': {
    '@mediaMinMedium': {
      flexWrap: 'nowrap',
    },
  },
  backgroundColor: 'unset',
});

const GridItemCss = {
  '@mediaMinLarge': {
    '&:nth-child(0)': {
      '& section': {
        top: '-79px',
      },
    },

    '&:nth-child(1)': {
      '& section': {
        top: '-79px',
      },
    },
  },
};

const SubHeadingWrapper = styled('div', {
  mb: 2,
  fs: 6,
  color: '$primaryLight3',
  lineHeight: '$lh133',
});

const BodyWrapper = styled('div', {
  mb: 8,
});

const Column = styled('div', {
  display: 'flex',
  width: '100%',
  '@mediaMinLarge': {
    py: 7.5,
  },
});

const ImageContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row-reverse',
  w: '100%',
  justifyContent: 'center',
  height: '100%',
});

const ImageArea = styled('section', {
  w: '100%',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  position: 'relative',
  order: 0,
  variants: {
    isSplitImage: {
      true: {
        minWidth: '150px',
        '@mediaMinHuge': {
          width: '100%',
        },
      },
      false: {
        width: '100%',
        '@mediaMinHuge': {
          width: '100%',
        },
      },
    },
  },
  height: '238px',
  '@mediaMinMedium': {
    height: '431px',
  },
});

const SecondaryImageArea = styled('section', {
  w: '192px',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  marginRight: '8px',
  position: 'relative',
  order: 0,
  '@mediaMinMedium': {
    marginRight: '32px',
  },
});

const TextContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  pt: 5,
  pb: 16,
  px: 4,
  w: '100%',
  color: '$textPrimary',
  '@mediaMinLarge': {
    py: 16,
    px: 8,
  },
  variants: {
    rightPositioned: {
      false: {
        '@mediaMinLarge': {
          pl: 26,
        },
      },
    },
  },
});

const TextWrapper = styled('div', {
  wordBreak: 'break-word',
  '@mediaMinLarge': {
    width: '416px',
  },
});

const Heading = styled('h2', {
  ...baseHeadingStyle,
  fs: 12,
  lineHeight: '$lh116',
  fontFamily: '$fontPrimary400',
  mt: 0,
  mb: 4,
});
